import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import './ArticleProse.css';

// A link is internal when it stays on this app — relative paths and same-origin
// absolute URLs. Only genuinely external destinations get a new tab; forcing
// target="_blank" on everything punched in-app CTAs like /succession/plans out
// into a new window.
const isExternalHref = (href) => {
  if (!href) return false;
  if (/^(mailto:|tel:)/i.test(href)) return true;
  if (/^(https?:)?\/\//i.test(href)) {
    try {
      return new URL(href, window.location.href).origin !== window.location.origin;
    } catch (e) {
      return true;
    }
  }
  return false;
};

// Paragraphs that are nothing but emphasis are the trailing legal disclaimers,
// not prose. Whitespace-only text nodes are ignored so `*text*` on its own line
// still matches.
const isEmphasisOnly = (node) => {
  const children = (node?.children || []).filter(
    child => !(child.type === 'text' && !child.value.trim())
  );
  return children.length === 1
    && children[0].type === 'element'
    && children[0].tagName === 'em';
};

const MarkdownViewer = ({ body }) => {
  return (
    <div className="fb-article">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Which note carries the dividing rule is decided in CSS by adjacency,
          // so this stays a pure function of the node.
          p: ({ node, children, ...props }) => (
            isEmphasisOnly(node)
              ? <p {...props} className="fb-article__note">{children}</p>
              : <p {...props}>{children}</p>
          ),
          table: ({ node, ...props }) => (
            <div className="fb-article__table-scroll">
              <table {...props} className="table-auto w-full border-collapse border border-succession-border text-sm" />
            </div>
          ),
          thead: ({ node, ...props }) => (
            <thead {...props} className="bg-succession-bg-secondary" />
          ),
          th: ({ node, ...props }) => (
            <th {...props} className="border border-succession-border px-3 py-2 text-left font-semibold text-succession-text-primary" />
          ),
          td: ({ node, ...props }) => (
            <td {...props} className="border border-succession-border px-3 py-2 align-top" />
          ),
          a: ({ node, href, ...props }) => (
            isExternalHref(href)
              ? <a {...props} href={href} target="_blank" rel="noopener noreferrer" />
              : <a {...props} href={href} />
          ),
          img: ({ node, ...props }) => (
            <img {...props} alt={props.alt || ''} />
          ),
        }}
      >
        {body || ''}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownViewer;
